import { useState, useRef, useEffect } from "react"
import { Play, Pause } from "lucide-react"

interface Props {
  autoWidth?: string;
  wavesWitdh?: string;
  src: string;
  title?: string;
  showWavesDefault?: boolean;
  externalHTML?: any;
}

export default function AudioPlayer({ autoWidth, wavesWitdh, src, title, showWavesDefault, externalHTML }: Props) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const setAudioData = () => {
      setDuration(audio.duration)
    }

    const setAudioTime = () => {
      setCurrentTime(audio.currentTime)
    }

    audio.addEventListener("loadeddata", setAudioData)
    audio.addEventListener("timeupdate", setAudioTime)

    return () => {
      audio.removeEventListener("loadeddata", setAudioData)
      audio.removeEventListener("timeupdate", setAudioTime)
    }
  }, [])

  const togglePlay = () => {
    const audio = audioRef.current
    if (!audio) return

    if (isPlaying) {
      audio.pause()
    } else {
      audio.play()
    }
    setIsPlaying(!isPlaying)
  }

  const formatTime = (time: number) => {
    if (isNaN(time)) return "0:00"
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`
  }

  const generateWaveform = (containerWidth = 50) => {
    const barWidth = 1
    const barMargin = 1 // on each side
    const totalBarSpace = barWidth + barMargin * 2
    const totalBars = Math.floor(containerWidth / totalBarSpace)

    const bars = []
    for (let i = 0; i < totalBars; i++) {
      let height
      if (i > totalBars * 0.3 && i < totalBars * 0.7) {
        height = Math.random() * 0.8 + 0.2
      } else {
        height = Math.random() * 0.5 + 0.1
      }

      bars.push(
        <div
          key={i}
          className="w-[1px] mx-[1px] rounded-full bg-amber-500"
          style={{ height: `${height * 24}px` }}
        />
      )
    }

    return bars
  }

  return (
    <div onClick={togglePlay} className="cursor-pointer">
      {!!externalHTML ? externalHTML : <div className={`bg-[#D0D9E6] py-1 px-3 rounded-3xl flex items-center gap-2 ${!!autoWidth ? autoWidth : "w-fit"}`}>
        <button className="w-fit">
          {isPlaying ? <Pause size={18} fill="#144582" /> : <Play size={18} fill="#144582" />}
        </button>

        <div className="text-[1.8vh] text-[black] font-medium min-w-[40px]">{title}</div>

        {(!!isPlaying || showWavesDefault) && <div className={`flex h-6 ${!!wavesWitdh ? wavesWitdh : "max-w-[50px]"} justify-between items-center overflow-hidden`}>
          {generateWaveform(50)}
        </div>}
      </div>}

      <audio ref={audioRef} className="hidden">
        <source
          src={src || "https://soomuch.blob.core.windows.net/data-soomuch/DemoContent/DemoEvents/bhagat_singh_podcast.wav"}
          type="audio/mpeg"
        />
        Your browser does not support the audio element.
      </audio>
    </div>
  )
}
